import React from 'react';
import CreateStandardTaskForm from './Standard/CreateStandardTaskForm';
import useAuthorizationChecker from '../../../../shared/authorization/authorizationChecker';
import CreateEmailTaskForm from './Email/CreateEmailTaskForm';
import { useProjectContext } from '../../ProjectContext';
import CreateSmsTaskForm from './Sms/CreateSmsTaskForm';
import { usePopupFormManager } from 'features/nekst-widgets';
import { ProjectPermission, TaskType, TeamPermission } from 'nekst-api';
import {
  ButtonSize,
  OptionSelectionButton,
  SelectableOption,
  SelectableOptionItem,
  SelectableOptionsGroup
} from 'ui-builder';
import CreateEventTaskForm from './Event/CreateEventTaskForm';
import useFeatureToggleService, { ToggledFeature } from 'feature-toggle';
import { EventTaskConfigurationInfoPopup } from 'features/tasks-forms';
import useSettingsNavigator from '../../../../settings/settingsNavigator';
import { useSetupAreaRequiredButtonsHelper } from '../../../../settings/markets/setupAreaRequiredHelper';
import CreateNoteForm from '../../notes/CreateNoteForm';

function useCreateTaskButtonOptions(): SelectableOptionItem<TaskType | 'note'>[] | SelectableOptionsGroup<TaskType | 'note'>[] | undefined {

  const { isGranted } = useAuthorizationChecker();

  const popupManager = usePopupFormManager();

  const {
    isWithinContext: isWithinProjectContext,
    data: project
  } = useProjectContext();


  const projectId = project?.id;

  const featureToggleService = useFeatureToggleService();
  const settingsNavigator = useSettingsNavigator();

  const canCreateTasks = () => {
    if (isWithinProjectContext()) {
      return isGranted(ProjectPermission.CREATE_TASKS);
    } else {
      return isGranted(TeamPermission.CREATE_PROJECT_TASKS);
    }
  };


  const canCreateNotes = isWithinProjectContext() &&
    isGranted(ProjectPermission.CREATE_NOTE) &&
    featureToggleService.isFeatureEnabled(ToggledFeature.CREATE_NOTE_FROM_TRANSACTION_PAGE);


  let createEventTaskOption: SelectableOption<TaskType> | undefined = undefined;
  if (featureToggleService.isFeatureEnabled(ToggledFeature.EVENT_TASKS)) {
    if (isGranted(TeamPermission.MANAGE_EVENT_TASKS)) {
      createEventTaskOption = {
        label: 'Appointment Task',
        value: TaskType.EVENT,
        onSelected: () => {
          popupManager.openForm(
            <CreateEventTaskForm projectId={projectId} />,
          );
        }
      };
    } else {
      createEventTaskOption = {
        label: 'Appointment Task',
        value: TaskType.EVENT,
        onSelected: () => {
          popupManager.openForm(
            <EventTaskConfigurationInfoPopup
              openSettingsFunc={settingsNavigator.openCalendarIntegrationPage}
            />,
          );
        }
      };
    }
  }


  const tasksOptions: SelectableOptionItem<TaskType>[] = [
    {
      label: 'Standard Task',
      value: TaskType.STANDARD,
      onSelected: () => {
        popupManager.openForm(
          <CreateStandardTaskForm projectId={projectId} />,
        );
      },
    },
    {
      label: 'Email Task',
      value: TaskType.EMAIL,
      onSelected: () => {
        popupManager.openForm(
          <CreateEmailTaskForm projectId={projectId} />,
        );
      }
    },
    isGranted(TeamPermission.USE_SMS_TASKS_FEATURE) && {
      label: 'SMS Task',
      value: TaskType.SMS,
      onSelected: () => {
        popupManager.openForm(
          <CreateSmsTaskForm projectId={projectId} />,
        );
      }
    },
    createEventTaskOption || false,
  ];

  const notesOptions: SelectableOptionItem<'note'>[] = projectId ? [
    {
      label: 'Note',
      value: 'note' as const,
      onSelected: () => {
        popupManager.openForm(
          <CreateNoteForm
            projectId={projectId}
            afterSubmitFunc={async () => {
              popupManager.closePopup();
            }}
          />,
        );
      }
    }
  ] : [];


  if (canCreateTasks() && canCreateNotes) {
    return [
      {
        title: 'Tasks',
        options: tasksOptions
      },
      {
        title: 'Note',
        options: notesOptions
      }
    ] as SelectableOptionsGroup<TaskType | 'note'>[];
  } else if (canCreateTasks()) {
    return tasksOptions as SelectableOptionItem<TaskType | 'note'>[];
  } else if (canCreateNotes) {
    return notesOptions as SelectableOptionItem<TaskType | 'note'>[];
  }
}

export default function CreateTaskButton() {

  const options = useCreateTaskButtonOptions();


  const featureToggleService = useFeatureToggleService();

  const { isDisabled: isButtonDisabled, title: buttonTitle } = useSetupAreaRequiredButtonsHelper();

  if (options && options.length) {
    return (
      <OptionSelectionButton<TaskType | 'note'>
        text={featureToggleService.isFeatureEnabled(ToggledFeature.CREATE_NOTE_FROM_TRANSACTION_PAGE) ? 'Add' : 'Add Task'}
        options={options}
        size={ButtonSize.MEDIUM}
        testId="add-task-button"
        isDisabled={isButtonDisabled}
        title={buttonTitle}
      />
    );
  } else {
    return null;
  }
}
